"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { TippoLoadingIndicator } from "@/components/ui/tippo-loading";
import { mapAuthError } from "@/lib/auth/map-auth-error";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";
import { trackAnalyticsEvent } from "@/lib/analytics/track";

export type TSignupLegalProps = Readonly<{
  terms: { id: string; version: string };
  privacy: { id: string; version: string };
  /** Redirect path after signup (e.g. invite accept URL). Defaults to /onboarding. */
  redirectTo?: string;
  /** Pre-fill email (e.g. from invite link). */
  initialEmail?: string;
}>;

function isInviteAcceptRedirect(next: string): boolean {
  try {
    const url = new URL(next, "https://example.invalid");
    return (
      url.pathname === "/invites/accept" ||
      url.pathname.startsWith("/invites/accept/")
    );
  } catch {
    return false;
  }
}

function resolveSafeRedirectPath(next: string, fallback = "/onboarding"): string {
  /**
   * `redirectTo` can be influenced by `?next=...` (e.g. invite links).
   * Only allow same-origin, absolute-in-app paths to avoid external/protocol-relative redirects.
   */
  if (typeof window === "undefined") return fallback;
  try {
    const url = new URL(next, window.location.origin);
    if (url.origin !== window.location.origin) return fallback;
    if (!url.pathname.startsWith("/")) return fallback;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

function toSafeAnalyticsErrorCode(value: unknown, fallback: string): string {
  /**
   * Analytics payloads must not include free-text error messages (which can drift into PII).
   * Prefer stable error codes when available; otherwise use a generic fallback.
   */
  if (!value || typeof value !== "object") return fallback;
  const obj = value as Record<string, unknown>;
  const code = obj.code;
  if (typeof code === "string" && code.trim().length > 0) return code;
  const status = obj.status;
  if (typeof status === "number" && Number.isFinite(status)) return `status_${status}`;
  const name = obj.name;
  if (typeof name === "string" && name.trim().length > 0) return name;
  return fallback;
}

export function SignupForm({
  terms,
  privacy,
  redirectTo = "/onboarding",
  initialEmail = "",
}: TSignupLegalProps): React.ReactElement {
  const router = useRouter();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Set when signup succeeds but no session (email confirmation required). */
  const [signupSuccessEmail, setSignupSuccessEmail] = useState<string | null>(
    null
  );

  const supabase = useMemo(() => createSupabaseBrowserClient(), []);
  const lockInviteEmail =
    Boolean(initialEmail) && isInviteAcceptRedirect(redirectTo);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  async function handleSignUp(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!accepted) {
      setError("Please accept the Terms and Privacy Policy to continue.");
      return;
    }

    setLoading(true);
    const safeRedirectTo = resolveSafeRedirectPath(redirectTo, "/onboarding");
    trackAnalyticsEvent("start_signup", {
      redirect_to: safeRedirectTo,
    });
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const callbackNext = safeRedirectTo;
    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName.trim() },
        emailRedirectTo: `${origin}/auth/callback?next=${encodeURIComponent(callbackNext)}`,
      },
    });

    if (err) {
      setLoading(false);
      setError(mapAuthError(err));
      trackAnalyticsEvent("signup_error", {
        error_code: toSafeAnalyticsErrorCode(err, "signup_failed"),
      });
      return;
    }

    // Email confirmation required: no session yet. Show success state; user will reach redirectTo (or onboarding) after clicking the link.
    if (!data.session) {
      setLoading(false);
      setSignupSuccessEmail(email);
      trackAnalyticsEvent("complete_signup", { email_confirm_required: true });
      return;
    }

    // Session returned: record legal acceptance and redirect to onboarding.
    if (data.user) {
      const nowIso = new Date().toISOString();
      const { error: upsertErr } = await supabase
        .from("legal_acceptances")
        .upsert(
          {
            user_id: data.user.id,
            terms_doc_id: terms.id,
            terms_version: terms.version,
            terms_accepted_at: nowIso,
            privacy_doc_id: privacy.id,
            privacy_version: privacy.version,
            privacy_accepted_at: nowIso,
            updated_at: nowIso,
          },
          { onConflict: "user_id" }
        );

      if (upsertErr) {
        // Don't block signup; the dashboard prompt will still require acceptance.
        console.warn("legal_acceptances upsert failed", upsertErr);
      }
    }

    /**
     * Intentionally do NOT call setLoading(false) here. Once the session
     * exists we're navigating away; clearing `loading` first would flicker
     * the Sign-up form back in for the duration of router.push (a few
     * hundred ms while the destination server-renders), which users
     * misread as "the button reappeared so my click didn't take".
     */
    trackAnalyticsEvent("complete_signup", { email_confirm_required: false });
    router.push(safeRedirectTo);
    router.refresh();
  }

  if (signupSuccessEmail) {
    return (
      <div
        className="rounded border border-border bg-card p-6 text-center"
        role="status"
      >
        <h2 className="text-lg font-semibold text-foreground">
          Check your email
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          We sent a confirmation link to <strong>{signupSuccessEmail}</strong>.
          Click it to finish setting up your account.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card p-10"
        role="status"
        aria-live="polite"
      >
        <TippoLoadingIndicator label="Creating your account" size={96} />
        <p className="text-sm text-muted-foreground">
          Hang tight — we&apos;re setting up your account.
        </p>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSignUp} className="space-y-4">
        <Field>
          <FieldLabel htmlFor="signup-fullname">Full name</FieldLabel>
          <Input
            id="signup-fullname"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoComplete="name"
            placeholder="Jane Doe"
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="signup-email">Email</FieldLabel>
          <Input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            readOnly={lockInviteEmail}
            disabled={lockInviteEmail}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="signup-password">Password</FieldLabel>
          <Input
            id="signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
          />
        </Field>

        <div className="flex items-center justify-start gap-6 rounded-md border border-border bg-card p-3">
          <Checkbox
            id="signup-accept"
            checked={accepted}
            onCheckedChange={(v) => setAccepted(Boolean(v))}
            aria-describedby="signup-accept-description"
          />
          <Label
            htmlFor="signup-accept"
            className="text-sm leading-6 text-foreground"
            id="signup-accept-description"
          >
            I Accept the{" "}
            <Link
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="font-medium underline underline-offset-4"
            >
              Terms
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              className="font-medium underline underline-offset-4"
            >
              Privacy Policy
            </Link>
            .
          </Label>
        </div>

        <Button
          type="submit"
          disabled={loading}
          aria-busy={loading}
          className="w-full"
        >
          {loading ? (
            <>
              <span
                aria-hidden="true"
                className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
              />
              Creating account…
            </>
          ) : (
            "Sign up"
          )}
        </Button>
      </form>
    </>
  );
}
